import React from 'react';
import WidgetToolbar from 'widgets/WidgetToolbar/WidgetToolbar';

import './CorporateActionsToolbar.scss';
import WidgetToolbarShowHidePanel from 'widgets/WidgetToolbarShowHidePanel/WidgetToolbarShowHidePanel';
import {
    useCorporateActionsAlerts,
    useCorporateActionsPortfolioRequirements,
    useUnFilteredCorporateActionsData,
} from 'widgets/corpActions/widget/hooks';
import { GridExportButton } from '@instinet-analytics/semantic-ui-components';
import { ToolbarIcon } from '@instinet-analytics/semantic-ui-components';
import { GridApi } from '@instinet-analytics/semantic-ui-components';
import CorporateActionsFiltersModal from 'widgets/corpActions/widget/toolbar/CorporateActionsFiltersModal';
import CorporateActionColumnPicker from 'widgets/corpActions/widget/toolbar/CorporateActionsColumnPicker';
import { CorporateActionsTypeDropdown } from 'widgets/corpActions/widget/toolbar/CorporateActionsTypeDropdown';
import { CorporateActionsDateQuickFilter } from 'widgets/corpActions/widget/toolbar/CorporateActionsDateQuickFilter';
import { useCorporateActionsConfigSlice } from 'widgets/corpActions/widget/store/config/configSlice';
import { CorporateActionsSettings } from 'widgets/corpActions/widget/settings/CorporateActionsSettings';
import { ControlledSelectionStatus } from 'portfolio/components/SelectionStatus';
import classNames from 'classnames';
import { SymbolEventsTypeValue } from 'widgets/corpActions/api/SymbolEventsRequest';
import { ShareWidgetConfigToolbarButton } from 'widgets/configSharing/shareWidgetConfigModal/ShareWidgetConfigToolbarButton';
import { useWidgetEditedColumnNames } from 'grid/columnNames/hooks';

export interface CorporateActionsToolbarProps {
    gridApi?: GridApi;
}
export default function CorporateActionsToolbar(
    props: CorporateActionsToolbarProps
) {
    const { gridApi } = props;

    const { config, dispatchConfigChanged } = useCorporateActionsConfigSlice();
    const {
        caTypes,
        excludeHistorical,
        excludeUpcoming,
        excludeToday,
        filters,
        visibleColumns,
    } = config;

    const data = useUnFilteredCorporateActionsData();
    const { editedColumnNames } = useWidgetEditedColumnNames();

    const messages = useCorporateActionsAlerts();
    const filterApplied = filters.length > 0;

    const upcoming = !excludeUpcoming;
    const historical = !excludeHistorical;
    const today = !excludeToday;

    const onDateQuickFilterChange = (next: {
        upcoming: boolean;
        historical: boolean;
        today: boolean;
    }) => {
        dispatchConfigChanged({
            excludeHistorical: !next.historical,
            excludeUpcoming: !next.upcoming,
            excludeToday: !next.today,
        });
    };

    const onChangeCaTypes = (nextCaTypes: Array<SymbolEventsTypeValue>) => {
        dispatchConfigChanged({ caTypes: nextCaTypes });
    };

    const requirements = useCorporateActionsPortfolioRequirements();

    const cssClasses = classNames(
        'CorporateActionsToolbar',
        'panel-header',
        !requirements.meetsRequirements && 'show-selection-status'
    );

    return (
        <WidgetToolbarShowHidePanel mustShow={!requirements.meetsRequirements}>
            <div className={cssClasses}>
                {!requirements.meetsRequirements && (
                    <div className="toolbar">
                        <ControlledSelectionStatus
                            message={requirements.alert!}
                        />
                    </div>
                )}
                {requirements.meetsRequirements && (
                    <div className="toolbar">
                        <CorporateActionsTypeDropdown
                            onChange={onChangeCaTypes}
                            selectedValues={caTypes}
                        />
                        <div className="separator" />
                        <CorporateActionsDateQuickFilter
                            upcoming={upcoming}
                            historical={historical}
                            today={today}
                            onChange={onDateQuickFilterChange}
                        />
                    </div>
                )}
                <WidgetToolbar
                    messages={messages}
                    settingsHeader={`Events Settings`}
                    settingsBody={<CorporateActionsSettings />}
                    settingsTriggerText={`Events Settings`}
                    settingsClassName="CorporateActionsSettingsModal"
                    fixedItems={
                        <>
                            {requirements.meetsRequirements && (
                                <GridExportButton
                                    csvFileName={'Events'}
                                    gridApi={gridApi}
                                    editedColumnNames={editedColumnNames}
                                />
                            )}
                            <CorporateActionsFiltersModal
                                key="filters"
                                filters={filters}
                                visibleColumns={visibleColumns}
                                data={data}
                                trigger={
                                    <div className="FiltersModalTrigger">
                                        <ToolbarIcon name="filter" />
                                        {filterApplied && (
                                            <div className="filter-indicator" />
                                        )}
                                    </div>
                                }
                            />

                            <CorporateActionColumnPicker
                                visibleColumns={visibleColumns}
                            />
                            <ShareWidgetConfigToolbarButton />
                        </>
                    }
                />
            </div>
        </WidgetToolbarShowHidePanel>
    );
}
